// src/UXAgentProvider.tsx
import { createContext, useContext, useMemo } from "react";
import { CopilotKit } from "@copilotkit/react-core";
import { CopilotSidebar } from "@copilotkit/react-ui";

// src/types.ts
var defaultDesignTokens = {
  colors: {
    primary: "#3b82f6",
    secondary: "#8b5cf6",
    accent: "#06b6d4",
    background: "#0a0a0f",
    surface: "#1a1a2e",
    text: "#ffffff",
    textMuted: "#9ca3af",
    border: "#2a2a4a",
    success: "#10b981",
    warning: "#f59e0b",
    error: "#ef4444"
  },
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem"
  },
  borderRadius: {
    sm: "0.25rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    full: "9999px"
  },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem"
  },
  shadows: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.1)"
  }
};

// src/UXAgentProvider.tsx
import "@copilotkit/react-ui/styles.css";
import { jsx } from "react/jsx-runtime";
var UXAgentContext = createContext(null);
function UXAgentProvider({ config, children, showSidebar = true }) {
  const designTokens = useMemo(
    () => ({ ...defaultDesignTokens, ...config.designSystem }),
    [config.designSystem]
  );
  const contextValue = useMemo(() => ({ config, designTokens }), [config, designTokens]);
  if (!config.apiKey) {
    return /* @__PURE__ */ jsx(UXAgentContext.Provider, { value: contextValue, children });
  }
  return /* @__PURE__ */ jsx(UXAgentContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx(CopilotKit, { publicApiKey: config.apiKey, children: showSidebar ? /* @__PURE__ */ jsx(
    CopilotSidebar,
    {
      defaultOpen: false,
      labels: {
        title: "UX Assistant",
        initial: `Hi! I'm your UX assistant. I can help you:

\u2022 Generate UI components
\u2022 Suggest design improvements
\u2022 Check accessibility issues
\u2022 Create responsive layouts
\u2022 Apply design tokens consistently

What would you like help with?`
      },
      clickOutsideToClose: true,
      children
    }
  ) : children }) });
}
function useUXAgent() {
  const context = useContext(UXAgentContext);
  if (!context) {
    throw new Error("useUXAgent must be used within UXAgentProvider");
  }
  return context;
}

// src/hooks/useComponentGenerator.ts
import { useState, useCallback } from "react";
import { useCopilotAction } from "@copilotkit/react-core";
function useComponentGenerator() {
  const { designTokens, config } = useUXAgent();
  const [generatedComponents, setGeneratedComponents] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  useCopilotAction({
    name: "generateComponent",
    description: "Generate a React component based on a description",
    parameters: [
      {
        name: "description",
        type: "string",
        description: "Description of the component to generate",
        required: true
      },
      {
        name: "componentType",
        type: "string",
        description: "Type: form, card, list, modal, navigation, chart",
        required: false
      }
    ],
    handler: async ({ description, componentType }) => {
      setIsGenerating(true);
      try {
        const component = await generateComponent(description, componentType, designTokens, config.projectType);
        setGeneratedComponents((prev) => [...prev, component]);
        return `Generated ${component.name} component with ${component.dependencies.length} dependencies`;
      } finally {
        setIsGenerating(false);
      }
    }
  });
  const generate = useCallback(
    async (description, componentType) => {
      setIsGenerating(true);
      try {
        const component = await generateComponent(description, componentType, designTokens, config.projectType);
        setGeneratedComponents((prev) => [...prev, component]);
        return component;
      } finally {
        setIsGenerating(false);
      }
    },
    [designTokens, config.projectType]
  );
  const clearComponents = useCallback(() => {
    setGeneratedComponents([]);
  }, []);
  return {
    generatedComponents,
    isGenerating,
    generate,
    clearComponents
  };
}
async function generateComponent(description, componentType, designTokens, projectType) {
  const name = description.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join("").replace(/[^a-zA-Z]/g, "");
  const isReactNative = projectType === "react-native";
  const code = isReactNative ? `import { View, Text, StyleSheet } from 'react-native'

export function ${name}() {
  return (
    <View style={styles.container}>
      <Text style={styles.text}>${description}</Text>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    backgroundColor: '${designTokens?.colors?.surface || "#1a1a2e"}',
    borderRadius: 12,
  },
  text: {
    color: '${designTokens?.colors?.text || "#ffffff"}',
  },
})` : `import { cn } from '@coherence/ui'

interface ${name}Props {
  className?: string
}

export function ${name}({ className }: ${name}Props) {
  return (
    <div className={cn(
      "p-4 rounded-lg",
      "bg-[${designTokens?.colors?.surface || "#1a1a2e"}]",
      "border border-[${designTokens?.colors?.border || "#2a2a4a"}]",
      className
    )}>
      <p className="text-white">${description}</p>
    </div>
  )
}`;
  return {
    name,
    code,
    dependencies: isReactNative ? ["react-native"] : ["@coherence/ui"]
  };
}

// src/hooks/useDesignSystem.ts
import { useMemo as useMemo2 } from "react";
import { useCopilotReadable } from "@copilotkit/react-core";
function useDesignSystem() {
  const { designTokens } = useUXAgent();
  useCopilotReadable({
    description: "Current design system tokens including colors, spacing, and typography",
    value: designTokens
  });
  const cssVariables = useMemo2(() => {
    return Object.entries(designTokens.colors).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [`--color-${key}`]: value
      }),
      {}
    );
  }, [designTokens]);
  const tailwindConfig = useMemo2(() => {
    return {
      theme: {
        extend: {
          colors: designTokens.colors,
          spacing: designTokens.spacing,
          borderRadius: designTokens.borderRadius,
          fontSize: designTokens.fontSizes,
          boxShadow: designTokens.shadows
        }
      }
    };
  }, [designTokens]);
  return {
    tokens: designTokens,
    cssVariables,
    tailwindConfig
  };
}

// src/hooks/useAccessibility.ts
import { useState as useState2, useCallback as useCallback2 } from "react";
import { useCopilotAction as useCopilotAction2 } from "@copilotkit/react-core";
function useAccessibility() {
  const [issues, setIssues] = useState2([]);
  const [isChecking, setIsChecking] = useState2(false);
  useCopilotAction2({
    name: "checkAccessibility",
    description: "Check component code for accessibility issues",
    parameters: [
      {
        name: "code",
        type: "string",
        description: "The component code to check",
        required: true
      }
    ],
    handler: async ({ code }) => {
      setIsChecking(true);
      try {
        const foundIssues = await runAccessibilityCheck(code);
        setIssues(foundIssues);
        return `Found ${foundIssues.length} accessibility issues: ${foundIssues.filter((i) => i.severity === "error").length} errors, ${foundIssues.filter((i) => i.severity === "warning").length} warnings`;
      } finally {
        setIsChecking(false);
      }
    }
  });
  const check = useCallback2(async (code) => {
    setIsChecking(true);
    try {
      const foundIssues = await runAccessibilityCheck(code);
      setIssues(foundIssues);
      return foundIssues;
    } finally {
      setIsChecking(false);
    }
  }, []);
  const clearIssues = useCallback2(() => {
    setIssues([]);
  }, []);
  const getIssueSummary = useCallback2(() => {
    return {
      total: issues.length,
      errors: issues.filter((i) => i.severity === "error").length,
      warnings: issues.filter((i) => i.severity === "warning").length,
      info: issues.filter((i) => i.severity === "info").length
    };
  }, [issues]);
  return {
    issues,
    isChecking,
    check,
    clearIssues,
    getIssueSummary
  };
}
async function runAccessibilityCheck(code) {
  const issues = [];
  let idCounter = 0;
  const imgWithoutAlt = /<img(?![^>]*alt=)[^>]*>/gi;
  if (imgWithoutAlt.test(code)) {
    issues.push({
      id: `a11y-${++idCounter}`,
      severity: "error",
      rule: "img-alt",
      message: "Images must have alt text",
      element: "img",
      suggestion: "Add an alt attribute describing the image content"
    });
  }
  const clickableDiv = /onClick[^>]*>.*<\/div>/gi;
  if (clickableDiv.test(code)) {
    issues.push({
      id: `a11y-${++idCounter}`,
      severity: "warning",
      rule: "click-events-have-key-events",
      message: "Clickable elements should be buttons or have appropriate ARIA roles",
      element: "div",
      suggestion: 'Use a <button> element or add role="button" and keyboard handlers'
    });
  }
  const inputWithoutLabel = /<input(?![^>]*(aria-label|id=))[^>]*>/gi;
  if (inputWithoutLabel.test(code)) {
    issues.push({
      id: `a11y-${++idCounter}`,
      severity: "error",
      rule: "label",
      message: "Form inputs must have associated labels",
      element: "input",
      suggestion: "Add aria-label or associate with a <label> element"
    });
  }
  const lowContrastPattern = /text-(gray-[1-3]00|white).*bg-(white|gray-[1-3]00)/gi;
  if (lowContrastPattern.test(code)) {
    issues.push({
      id: `a11y-${++idCounter}`,
      severity: "warning",
      rule: "color-contrast",
      message: "Text may have insufficient color contrast",
      suggestion: "Ensure contrast ratio is at least 4.5:1 for normal text"
    });
  }
  const headings = code.match(/<h[1-6]/g) || [];
  if (headings.length > 1) {
    const levels = headings.map((h) => parseInt(h[2]));
    for (let i = 1; i < levels.length; i++) {
      if (levels[i] - levels[i - 1] > 1) {
        issues.push({
          id: `a11y-${++idCounter}`,
          severity: "warning",
          rule: "heading-order",
          message: "Heading levels should not skip levels",
          suggestion: `Heading jumps from h${levels[i - 1]} to h${levels[i]}`
        });
        break;
      }
    }
  }
  const buttonWithoutType = /<button(?![^>]*type=)[^>]*>/gi;
  if (buttonWithoutType.test(code)) {
    issues.push({
      id: `a11y-${++idCounter}`,
      severity: "info",
      rule: "button-has-type",
      message: "Buttons should have explicit type attribute",
      element: "button",
      suggestion: 'Add type="button" or type="submit"'
    });
  }
  const positiveTabindex = /tabindex=["']?[1-9]/gi;
  if (positiveTabindex.test(code)) {
    issues.push({
      id: `a11y-${++idCounter}`,
      severity: "warning",
      rule: "tabindex-no-positive",
      message: "Avoid positive tabindex values",
      suggestion: 'Use tabindex="0" or "-1" instead; manage focus order with DOM order'
    });
  }
  return issues;
}
export {
  UXAgentProvider,
  useAccessibility,
  useComponentGenerator,
  useDesignSystem,
  useUXAgent
};
